/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * QuestionDetailsCard
 *
 * @file  QuestionDetailsCard.jsx
 * @since 1.0.0
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Button, Badge } from '@material-ui/core';
import MathTextComponent from '../common/MathTextComponent';
import useDifficultTopicStyles from './styles/DifficultTopicStyles';
import Icons from '../icons/Icon';
import { OverviewTemplate } from '../tabular/components/Template';
import ReviewQuestionModal from './ReportQuestionModal';

const QuestionDetailsCard = (props) => {
  const {
    questions,
    onFlaggedQuestionClick,
    onReviewItClick,
    onClose,
    open,
    onInaccurateClick,
    difficultTopicReviewData,
    reportQuestionReasons,
    reviewCount = 0,
    components,
    ...otherProps
  } = props;
  const classes = useDifficultTopicStyles(props);

  if (!questions) return null;

  const {
    chapterTitle,
    question_stem: questionStem,
    questionNumber,
    options = [],
    isFlagged
  } = questions;

  const getLetter = (index) => String.fromCharCode(65 + index);

  return (
    <>
      <Box className={classes.root} component="section">
        <Box className={classes.headerRow}>
          <Box className={classes.headerLeft}>
            <MathTextComponent
              text={chapterTitle}
              className={classes.chapterHeader}
              component="h3"
            />
          </Box>
          <Box className={classes.headerActions}>
            {isFlagged && (
              <span className={classes.flagButton}>
                <span className={classes.flagIcon}>
                  <Icons name="FlagIcon" />
                </span>
                {otherProps.textInfo.flaggedQuestionLabel}
              </span>
            )}

            {otherProps.textInfo.reviewItLabel && (
              <Badge
                badgeContent={reviewCount}
                invisible={reviewCount === 0}
                color="secondary"
                overlap="rectangular"
                className={classes.reviewBadge}
              >
                <Button
                  variant="contained"
                  className={classes.reviewButton}
                  onClick={onReviewItClick}
                >
                  {otherProps.textInfo.reviewItLabel}
                </Button>
              </Badge>
            )}
          </Box>
        </Box>
        {questionStem && (
          <div className={classes.questionStem}>
            {questionNumber && (
              <div className={classes.questionNumberWrapper}>
                <Typography className={classes.questionTitle}>{questionNumber}</Typography>
                <span className={classes.questionTitle}>:</span>
              </div>
            )}
            <OverviewTemplate
              name={questionStem}
              classes={{
                subtitle2: classes.questionTitle,
                primaryCol1: classes.primaryCol1
              }}
            />
          </div>
        )}

        {/* Options */}
        <Box className={classes.options}>
          {options.map((opt, index) => {
            const isCorrect = opt.correct_choice === 'Yes';
            const isSelected = opt.is_most_common_incorrect_response === true;

            // eslint-disable-next-line no-nested-ternary
            const rowClass = isCorrect
              ? classes.correctOption
              : isSelected
                ? classes.selectedOptions
                : '';

            // Determine prefix class
            // eslint-disable-next-line no-nested-ternary
            const prefixClass = isCorrect
              ? classes.prefixCorrect
              : isSelected
                ? classes.prefixWrong
                : '';

            // Determine label class
            // eslint-disable-next-line no-nested-ternary
            const labelClass = isCorrect
              ? classes.correctLabel
              : isSelected
                ? classes.wrongLabel
                : '';

            return (
              <Box key={opt.id} className={`${classes.optionRow} ${rowClass}`}>
                <Box
                  component="span"
                  className={`${classes.optionPrefix} ${prefixClass}`}
                >
                  {getLetter(index)}
                </Box>

                <Box className={classes.optionContent}>
                  <MathTextComponent
                    text={opt.distractor || ''}
                    className={`${classes.optionLabel} ${labelClass}`}
                  />

                  {isCorrect && (
                    <Typography className={classes.correctSubLabel}>
                      {otherProps.textInfo.correctAnswer}
                    </Typography>
                  )}

                  {!isCorrect && isSelected && (
                    <Typography className={classes.wrongSubLabel}>
                      {otherProps.textInfo.incorrectAnswer}
                    </Typography>
                  )}
                </Box>

                {isCorrect && <Icons name="PositiveIcon" />}
                {!isCorrect && isSelected && <Icons name="NegativeIcon" />}
              </Box>
            );
          })}
        </Box>

        {components?.viewQuestions ? (
          <Box className={classes.viewQuestionsFooter}>
            {components.viewQuestions}
          </Box>
        ) : null}
      </Box>
      <ReviewQuestionModal
        onClose={onClose}
        open={open}
        reviewData={difficultTopicReviewData}
        onInaccurateClick={onInaccurateClick}
        textInfo={otherProps.textInfo}
        reportQuestionReasons={reportQuestionReasons}
      />
    </>
  );
};

QuestionDetailsCard.propTypes = {
  questions: PropTypes.shape({
    chapterTitle: PropTypes.string,
    question_stem: PropTypes.string.isRequired,
    options: PropTypes.array.isRequired,
    isFlagged: PropTypes.bool,
    questionNumber: PropTypes.string
  }).isRequired,
  onFlaggedQuestionClick: PropTypes.func,
  onReviewItClick: PropTypes.func,
  textInfo: PropTypes.shape({
    flaggedQuestionLabel: PropTypes.string,
    reviewItLabel: PropTypes.string,
    correctAnswer: PropTypes.string,
    incorrectAnswer: PropTypes.string
  }).isRequired,
  onClose: PropTypes.func,
  open: PropTypes.bool,
  onInaccurateClick: PropTypes.func,
  difficultTopicReviewData: PropTypes.object,
  reportQuestionReasons: PropTypes.array,
  reviewCount: PropTypes.number,
  components: PropTypes.shape({
    /** Optional node from parent — shown left of Report question */
    viewQuestions: PropTypes.node
  })
};

QuestionDetailsCard.defaultProps = {
  onFlaggedQuestionClick: () => {},
  onReviewItClick: () => {},
  onClose: () => {},
  open: false,
  onInaccurateClick: () => {},
  difficultTopicReviewData: {},
  reportQuestionReasons: [],
  reviewCount: 0,
  components: null
};

export default QuestionDetailsCard;
